// src/pages/PermissionsPage.tsx — resource × CRUD matrix editor.
// Super-admin groups come from the env bootstrap and render as fixed
// full-access rows; everything else is editable (permissions:U) and stored
// in KV. Nothing here can strip super-admin access.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/useSession';
import { GroupPicker } from '../components/group-picker';
import { DEFAULT_GROUP_MAP } from '@eotir/oidcbridge-shared/forum-groups';
import {
  RBAC_RESOURCES,
  type PermissionsResponse,
  type RbacPermissionSet,
  type RbacResource,
} from '@eotir/oidcbridge-shared/api-contracts';

const ACTIONS = ['C', 'R', 'U', 'D'] as const;

const RESOURCE_LABELS: Record<RbacResource, string> = {
  clients: 'Clients',
  tombstones: 'Tombstones',
  secrets: 'Secrets',
  permissions: 'Permissions',
  changelog: 'Changelog',
};

type GroupsDraft = Record<string, RbacPermissionSet>;

export function PermissionsPage() {
  const { can } = useSession();
  const editable = can('permissions', 'U');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions'],
    queryFn: ({ signal }) => api<PermissionsResponse>('/api/permissions', { signal }),
  });

  const { data: groupData } = useQuery({
    queryKey: ['forum-groups'],
    queryFn: ({ signal }) =>
      api<{ groupMap: Record<string, string>; source: string }>('/api/forum-groups', { signal }),
    staleTime: 5 * 60_000,
  });
  const groupMap = groupData?.groupMap ?? DEFAULT_GROUP_MAP;

  const [draft, setDraft] = useState<GroupsDraft>({});
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (data && !dirty) setDraft(data.matrix?.groups ?? {});
  }, [data, dirty]);

  const saveMut = useMutation({
    mutationFn: (groups: GroupsDraft) =>
      api<PermissionsResponse>('/api/permissions', { method: 'PUT', body: { groups } }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['permissions'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  if (isLoading) return <p className="text-sm text-ink-3">Loading…</p>;
  if (error) {
    const apiErr = error as ApiError;
    return (
      <div>
        <div className="eo-eyebrow">Access Control</div>
        <h1 className="eo-h1 mb-4">Permissions</h1>
        <p className="text-sm text-err">
          {apiErr.status === 403
            ? 'Your role cannot view the permission matrix.'
            : `Failed to load: ${apiErr.message}`}
        </p>
      </div>
    );
  }
  if (!data) return null;

  const toggle = (groupId: string, resource: RbacResource, action: (typeof ACTIONS)[number]) => {
    if (!editable) return;
    setDraft((prev) => {
      const groupPerms = { ...(prev[groupId] ?? {}) };
      const current = groupPerms[resource] ?? '';
      const next = current.includes(action)
        ? current.replace(action, '')
        : ACTIONS.filter((a) => current.includes(a) || a === action).join('');
      if (next === '') delete groupPerms[resource];
      else groupPerms[resource] = next;
      return { ...prev, [groupId]: groupPerms };
    });
    setDirty(true);
  };

  const removeGroup = (groupId: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
    setDirty(true);
  };

  const matrixGroupIds = Object.keys(draft).sort((a, b) => Number(a) - Number(b));

  return (
    <div>
      <div className="eo-eyebrow">Access Control</div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="eo-h1">Permissions</h1>
        {editable && (
          <div className="flex gap-3">
            <button type="button" onClick={() => setPickerOpen(true)} className="eo-btn eo-btn--ghost">
              ＋ Add group
            </button>
            <button
              type="button"
              disabled={!dirty || saveMut.isPending}
              onClick={() => saveMut.mutate(draft)}
              className="eo-btn"
            >
              {saveMut.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </div>
        )}
      </div>
      <p className="eo-hint mb-5 max-w-3xl">
        Each forum group gets Create / Read / Update / Delete per resource. A group with no
        row (or no boxes ticked) cannot sign in to this admin at all. Changes apply to new
        requests immediately — including active sessions.
      </p>

      {saveMut.error && (
        <p className="text-sm text-err mb-3">{(saveMut.error as Error).message}</p>
      )}

      <table className="eo-table">
        <thead>
          <tr>
            <th>Group</th>
            {RBAC_RESOURCES.map((r) => <th key={r}>{RESOURCE_LABELS[r]}</th>)}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.superAdminGroups.map((gid) => (
            <tr key={`super-${gid}`}>
              <td>
                <span className="eo-mono">{groupMap[String(gid)] ?? `Group ${gid}`} ({gid})</span>
                <div><span className="eo-chip eo-chip--warn mt-1">Super-admin · env</span></div>
              </td>
              {RBAC_RESOURCES.map((r) => (
                <td key={r} className="font-mono text-[11px] text-warn-soft">CRUD</td>
              ))}
              <td className="text-right text-ink-4 text-[11px]">fixed</td>
            </tr>
          ))}
          {matrixGroupIds.map((gid) => (
            <tr key={gid}>
              <td><span className="eo-mono">{groupMap[gid] ?? `Group ${gid}`} ({gid})</span></td>
              {RBAC_RESOURCES.map((resource) => (
                <td key={resource}>
                  <div className="flex gap-1">
                    {ACTIONS.map((action) => {
                      const on = (draft[gid]?.[resource] ?? '').includes(action);
                      return (
                        <button
                          key={action}
                          type="button"
                          disabled={!editable}
                          onClick={() => toggle(gid, resource, action)}
                          title={`${action} on ${resource}`}
                          className={`font-mono text-[10px] w-5 h-5 border rounded ${
                            on
                              ? 'border-ops bg-ops/20 text-white'
                              : 'border-line text-ink-4 hover:text-ink-2'
                          } ${editable ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                          {action}
                        </button>
                      );
                    })}
                  </div>
                </td>
              ))}
              <td className="text-right">
                {editable && (
                  <button
                    type="button"
                    onClick={() => removeGroup(gid)}
                    className="font-mono text-[10px] tracking-label uppercase text-err-soft hover:text-white"
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
          {matrixGroupIds.length === 0 && (
            <tr>
              <td colSpan={RBAC_RESOURCES.length + 2} className="text-ink-3 text-[12.5px]">
                No additional groups configured — only super-admin groups can sign in.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {data.matrix && (
        <p className="font-mono text-[10px] tracking-label uppercase text-ink-4 mt-3">
          Last updated {new Date(data.matrix.updatedAt).toLocaleString()} by member #{data.matrix.updatedBy}
        </p>
      )}

      <GroupPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedGroups={matrixGroupIds}
        onSelectionChange={(selected) => {
          setDraft((prev) => {
            const next: GroupsDraft = {};
            for (const gid of selected) {
              // New groups start with read-only clients access.
              next[gid] = prev[gid] ?? { clients: 'R' };
            }
            return next;
          });
          setDirty(true);
        }}
        groupMap={groupMap}
        isLoading={false}
      />
    </div>
  );
}
